import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity, RefreshCw, Clock, Search, LogIn, LogOut, AlertTriangle,
  Fingerprint, CreditCard, UserCheck, User, Eye,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const getStatusIcon = (status) => {
  if (status === 0 || status === 8 || status === 9)   return <LogIn className="h-4 w-4 text-green-600" />;
  if (status === 1 || status === 15 || status === 16)  return <LogOut className="h-4 w-4 text-red-600" />;
  if (status === 255)                                   return <AlertTriangle className="h-4 w-4 text-orange-600" />;
  return <Clock className="h-4 w-4 text-muted-foreground" />;
};

const getStatusColor = (status) => {
  if (status === 0 || status === 8 || status === 9)   return 'bg-green-950/30 text-green-400';
  if (status === 1 || status === 15 || status === 16)  return 'bg-red-950/30 text-red-400';
  if (status === 255)                                   return 'bg-orange-950/30 text-orange-400';
  return 'bg-muted text-foreground';
};

const getVerificationIcon = (mode) => {
  switch (mode) {
    case 'fingerprint': return <Fingerprint className="h-4 w-4" />;
    case 'face':        return <UserCheck className="h-4 w-4" />;
    case 'card':        return <CreditCard className="h-4 w-4" />;
    default:            return <User className="h-4 w-4" />;
  }
};

const formatTimestamp = (timestamp, t, language) => {
  const date = new Date(
    typeof timestamp === 'string' && !timestamp.endsWith('Z') ? `${timestamp}Z` : timestamp
  );
  const diffMins = Math.floor((new Date() - date) / 60000);
  if (diffMins < 1)    return t('zktecoComp.justNow');
  if (diffMins < 60)   return t('zktecoComp.minutesAgo', { n: diffMins });
  if (diffMins < 1440) return t('zktecoComp.hoursAgo', { n: Math.floor(diffMins / 60) });
  return date.toLocaleDateString(language, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const getVerificationLabel = (mode, t) => {
  const labels = {
    fingerprint: t('zktecoComp.fingerprint'),
    face: t('zktecoComp.face'),
    card: t('zktecoComp.card'),
    password: 'PIN',
  };

  return labels[mode] || t('zktecoComp.unknown');
};

const PunchLogsCard = ({
  filteredLogs,
  loading,
  searchTerm,
  setSearchTerm,
  dateFilter,
  setDateFilter,
  statusFilter,
  setStatusFilter,
  deviceFilter,
  setDeviceFilter,
  terminals,
  wsConnected,
  autoRefresh,
  currentPage,
  setCurrentPage,
  totalPages,
}) => {
  const { t, i18n } = useTranslation();

  return (
  <Card>
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          {t('zktecoComp.realtimeFeed')}
          {wsConnected && (
            <Badge variant="secondary" className="bg-green-950/40 text-green-400">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2" />
              LIVE PUSH
            </Badge>
          )}
          {autoRefresh && !wsConnected && (
            <Badge variant="secondary" className="bg-muted text-muted-foreground">
              <Clock className="w-3 h-3 mr-1" />
              POLL
            </Badge>
          )}
        </CardTitle>
      </div>
    </CardHeader>
    <CardContent>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder={t('zktecoComp.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-32"><SelectValue placeholder={t('zktecoComp.period')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">{t('zktecoComp.today')}</SelectItem>
              <SelectItem value="yesterday">{t('zktecoComp.yesterday')}</SelectItem>
              <SelectItem value="week">{t('zktecoComp.last7Days')}</SelectItem>
              <SelectItem value="all">{t('zktecoComp.all')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32"><SelectValue placeholder={t('zktecoComp.direction')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('zktecoComp.allMasc')}</SelectItem>
              <SelectItem value="checkin">{t('zktecoComp.checkIns')}</SelectItem>
              <SelectItem value="checkout">{t('zktecoComp.checkOuts')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={deviceFilter} onValueChange={setDeviceFilter}>
            <SelectTrigger className="w-40"><SelectValue placeholder={t('zktecoComp.terminal')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('zktecoComp.allTerminals')}</SelectItem>
              {terminals.map((t) => (
                <SelectItem key={t.serial_number} value={t.serial_number}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="h-6 w-6 animate-spin mr-2" />
          <span>{t('zktecoComp.loadingPunches')}</span>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">{t('zktecoComp.direction')}</TableHead>
              <TableHead>{t('zktecoComp.employee')}</TableHead>
              <TableHead>{t('zktecoComp.timestamp')}</TableHead>
              <TableHead>{t('zktecoComp.terminal')}</TableHead>
              <TableHead>{t('zktecoComp.verification')}</TableHead>
              <TableHead>{t('zktecoComp.status')}</TableHead>
              <TableHead className="text-right">{t('zktecoComp.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLogs.map((log, index) => (
              <TableRow
                key={`${log.id}-${index}`}
                className={`${
                  index < 5 && log.is_realtime
                    ? 'bg-green-950/30 border-l-4 border-l-green-500 animate-pulse'
                    : index < 5 ? 'bg-muted/20' : ''
                } ${log.is_realtime ? 'transition-all duration-500' : ''}`}
              >
                <TableCell>
                  <div className="flex items-center justify-center">
                    {log.is_realtime && (
                      <div className="absolute -left-2 w-1 h-8 bg-green-500 rounded animate-pulse" />
                    )}
                    {getStatusIcon(log.status, log.verification_mode)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-semibold flex items-center gap-2">
                      {log.employee_display_name || log.employee_name || `ID ${log.user_id}`}
                      {log.is_realtime && (
                        <Badge variant="secondary" className="bg-green-950/40 text-green-400 text-xs animate-bounce">
                          LIVE
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {log.registration_number
                        ? t('zktecoComp.registrationNumber', { n: log.registration_number })
                        : log.employee_badge
                          ? t('zktecoComp.badgeNumber', { n: log.employee_badge })
                          : `ID ${log.user_id}`}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{new Date(log.timestamp).toLocaleString(i18n.language)}</div>
                    <div className="text-xs text-muted-foreground">{formatTimestamp(log.timestamp, t, i18n.language)}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{log.terminal_name || t('zktecoComp.unknownTerminal')}</div>
                    <div className="text-xs text-muted-foreground">SN: {log.device_sn}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getVerificationIcon(log.verification_mode)}
                    <Badge variant="secondary" className="text-xs">
                      {getVerificationLabel(log.verification_mode, t)}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(log.status)}>{log.attendance_direction}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {filteredLogs.length === 0 && !loading && (
        <div className="text-center py-8 text-muted-foreground">
          <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2">{t('zktecoComp.noPunchTitle')}</h3>
          <p>{t('zktecoComp.noPunchDesc')}</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 mt-4">
          <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
            {t('common.previous')}
          </Button>
          <span className="text-sm text-muted-foreground">{t('zktecoComp.pageOf', { page: currentPage, total: totalPages })}</span>
          <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
            {t('common.next')}
          </Button>
        </div>
      )}
    </CardContent>
  </Card>
  );
};

export default PunchLogsCard;
